import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { User } from 'app/core/user/user.types';
import { map, Observable, ReplaySubject, tap } from 'rxjs';

@Injectable({providedIn: 'root'})
export class UserService {
    private _httpClient = inject(HttpClient);
    private _user: ReplaySubject<User> = new ReplaySubject<User>(1);

    constructor() {
        // Attempt to load user from local storage on service initialization
        this.loadUserFromLocalStorage();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for user
     *
     * @param value
     */
    set user(value: User) {
        // Set default avatar
        value.avatar = value.avatar ?? 'assets/images/avatars/default.webp';

        // Store the value in the ReplaySubject
        this._user.next(value);
        // Also update local storage
        localStorage.setItem('user', JSON.stringify(value));
    }

    get user$(): Observable<User> {
        return this._user.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get the current signed-in user data
     */
    get(): Observable<User> {
        return this._httpClient.get<User>('api/common/user').pipe(
            tap((user) => {
                this._user.next(user);
                localStorage.setItem('user', JSON.stringify(user));
            }),
        );
    }

    /**
     * Update the user
     *
     * @param user
     */
    update(user: User): Observable<any> {
        return this._httpClient.patch<User>('api/common/user', {user}).pipe(
            map((response) => {
                this._user.next(response);
                localStorage.setItem('user', JSON.stringify(response));
                return response;
            }),
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Load user from local storage and emit through _user ReplaySubject
     */
    private loadUserFromLocalStorage(): void {
        const userData = localStorage.getItem('user');
        if (userData) {
            const user: User = JSON.parse(userData);
            this._user.next(user);
        }
    }
}
